import {
  Children,
  cloneElement,
  isValidElement,
  useState,
  type MouseEvent,
  type PropsWithChildren,
} from 'react';

import { cn } from '@mint-lib/tw-utils';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip.jsx';

/**
 * Truncates a given text if it exceeds its container's width.
 * If the text is truncated, it will display a tooltip with the full text.
 * @param {PropsWithChildren} props - React props with a single child.
 * @throws {Error} If the are more than one child.
 * @throws {TypeError} If the child does not contain a string.
 *
 * @example with plain text
 * <TruncateText>
 *   Long text
 * </TruncateText>
 *
 * @example with a nested component <Foo /> that returns a string
 * <TruncateText>
 *   // Please never write components in this way, this is only for example
 *   {() => <div>Long component</div>}
 * </TruncateText>
 */
type TruncateTextProps = PropsWithChildren<{
  tooltipClassName?: string;
}>;

export function TruncateText({
  children,
  tooltipClassName,
}: Readonly<TruncateTextProps>) {
  const [isEllipsis, setIsEllipsis] = useState(false);

  if (Children.count(children) > 1) {
    throw new Error("<TruncateText/> can't have more than one child");
  }

  if (!isValidElement(children)) {
    if (isEllipsis) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate">{children}</span>
          </TooltipTrigger>
          <TooltipContent
            align="center"
            side="top"
            sideOffset={10}
            className={cn('z-tooltip', tooltipClassName)}
          >
            {children}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <span
        onMouseEnter={(e: MouseEvent<HTMLElement>) => {
          const el = e.target as HTMLSpanElement;
          setIsEllipsis(el.offsetWidth < el.scrollWidth);
        }}
        className="truncate"
      >
        {children}
      </span>
    );
  }

  const childProps = children.props as any;

  if (typeof childProps.children !== 'string') {
    throw new TypeError('<TruncateText/> child must contain a string.');
  }

  if (isEllipsis) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {cloneElement(children, {
            ...childProps,
            className: cn(childProps.className, 'truncate'),
          })}
        </TooltipTrigger>
        <TooltipContent
          align="center"
          side="top"
          sideOffset={10}
          className={cn('z-tooltip', tooltipClassName)}
        >
          {childProps.children}
        </TooltipContent>
      </Tooltip>
    );
  }

  return cloneElement(children, {
    ...childProps,
    className: cn(childProps.className, 'truncate'),
    onMouseEnter: (e: MouseEvent<HTMLElement>) => {
      const el = e.target as HTMLElement;
      setIsEllipsis(el.offsetWidth < el.scrollWidth);
      childProps.onMouseEnter?.(e);
    },
  });
}
